import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import './SignaturePad.css';

/**
 * 線上簽名組件
 * 支援觸控和滑鼠手寫簽名，可儲存為 JPG 圖片
 */
function SignaturePad({ show, onHide, onSave }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // 設定 Canvas 尺寸（高解析度）
      const container = canvas.parentElement;
      const width = Math.min(container.clientWidth - 40, 600);
      const height = 300;

      // 設定顯示尺寸
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';

      // 設定實際繪圖尺寸（2倍解析度，提升圖片品質）
      canvas.width = width * 2;
      canvas.height = height * 2;

      // 縮放繪圖上下文
      ctx.scale(2, 2);

      // 清空畫布並設定白色背景
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);

      // 設定筆觸樣式
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      setIsEmpty(true);
      setError(null);
    }
  }, [show]);

  // 取得滑鼠/觸控位置
  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    if (e.touches) {
      // 觸控事件
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    } else {
      // 滑鼠事件
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  };

  // 開始繪製
  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPosition(e);

    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setIsEmpty(false);
  };

  // 繪製中
  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPosition(e);

    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  // 結束繪製
  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  // 清除簽名
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width / 2;
    const height = canvas.height / 2;

    // 清空畫布並設定白色背景
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);

    setIsEmpty(true);
    setError(null);
  };

  // 儲存簽名為 JPG
  const saveSignature = () => {
    if (isEmpty) {
      setError('請先簽名後再儲存');
      return;
    }

    try {
      const canvas = canvasRef.current;

      // 轉換為 Blob (JPG 格式，品質 0.9)
      canvas.toBlob((blob) => {
        if (blob) {
          // 建立 File 物件
          const timestamp = new Date().getTime();
          const file = new File([blob], `signature-${timestamp}.jpg`, {
            type: 'image/jpeg',
            lastModified: timestamp
          });

          // 回傳給父組件
          onSave(file);
          onHide();
        } else {
          setError('簽名儲存失敗，請重試');
        }
      }, 'image/jpeg', 0.9);

    } catch (err) {
      console.error('儲存簽名失敗:', err);
      setError('簽名儲存失敗：' + err.message);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>✍️ 線上簽名</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        <div className="signature-pad-instructions mb-3">
          <p className="text-muted mb-2">
            📱 請在下方白色區域簽署您的姓名
          </p>
          <ul className="text-muted small">
            <li>使用滑鼠或手指在白色區域簽名</li>
            <li>簽名將以 JPG 格式儲存</li>
            <li>點擊「清除」可重新簽名</li>
          </ul>
        </div>

        <div className="signature-pad-container">
          <canvas
            ref={canvasRef}
            className="signature-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>

        <div className="signature-pad-actions mt-3">
          <div className="text-muted small">
            💡 提示：建議在手機橫向模式下簽名，效果更佳
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={clearSignature}>
          🗑️ 清除
        </Button>
        <Button variant="secondary" onClick={onHide}>
          取消
        </Button>
        <Button variant="primary" onClick={saveSignature} disabled={isEmpty}>
          💾 儲存簽名
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SignaturePad;
